var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Button } from 'components/Button';
import { CaptchaWidget, isCaptchaValid } from 'components/captcha/CaptchaWidget';
import { Icon } from 'components/Icon';
import { Money } from 'components/Money';
import { ApplyPromoCodeMutation } from 'graphql/Deal/mutations/ApplyPromoCodeMutation';
import { RemovePromoCodeMutation } from 'graphql/Deal/mutations/RemovePromoCodeMutation';
import { React, useMutation, useState } from 'libs/react';
import { promoCodeErrorReason, promoCodeReasonMessage } from '../storePromoCode';
import './StorePromoCodeField.scss';
/**
 * Promo code input of the store order summary. The discount is computed by
 * the server: the totals come back with the updated cart.
 *
 * @component StorePromoCodeField
 * @since 1.0.0
 */
export function StorePromoCodeField(props) {
    const { deal, captcha, language, initialCode, disabled, onChange } = props;
    const [open, setOpen] = useState(!!initialCode);
    const [code, setCode] = useState(initialCode !== null && initialCode !== void 0 ? initialCode : '');
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    // A captcha token is single use: remount the widget after each attempt.
    const [captchaKey, setCaptchaKey] = useState(0);
    const [applyPromoCode] = useMutation(ApplyPromoCodeMutation);
    const [removePromoCode] = useMutation(RemovePromoCodeMutation);
    const promoCode = deal.promoCode;
    const canApply = !!code.trim() && !processing && isCaptchaValid(captcha, captchaToken);
    /**
     * @function handleApply
     * @since 1.0.0
     * @hidden
     */
    function handleApply() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (!canApply) {
                return;
            }
            setProcessing(true);
            setError(null);
            try {
                const result = yield applyPromoCode({
                    variables: {
                        input: {
                            id: deal.id,
                            code: code.trim(),
                            captchaToken: captchaToken !== null && captchaToken !== void 0 ? captchaToken : undefined
                        }
                    }
                });
                const updated = (_a = result.data) === null || _a === void 0 ? void 0 : _a.applyPromoCode;
                if (updated) {
                    setCode('');
                    setOpen(false);
                    onChange === null || onChange === void 0 ? void 0 : onChange(updated);
                }
            }
            catch (err) {
                const reason = promoCodeErrorReason(err);
                setError(reason
                    ? promoCodeReasonMessage(reason)
                    : ((_d = (_c = (_b = err === null || err === void 0 ? void 0 : err.graphQLErrors) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.message) !== null && _d !== void 0 ? _d : t('This promo code is invalid.')));
            }
            finally {
                setProcessing(false);
                setCaptchaToken(null);
                setCaptchaKey((key) => key + 1);
            }
        });
    }
    /**
     * @function handleRemove
     * @since 1.0.0
     * @hidden
     */
    function handleRemove() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            setProcessing(true);
            setError(null);
            try {
                const result = yield removePromoCode({
                    variables: { input: { id: deal.id } }
                });
                const updated = (_a = result.data) === null || _a === void 0 ? void 0 : _a.removePromoCode;
                if (updated) {
                    onChange === null || onChange === void 0 ? void 0 : onChange(updated);
                }
            }
            catch (err) {
                setError(promoCodeReasonMessage((_b = promoCodeErrorReason(err)) !== null && _b !== void 0 ? _b : ''));
            }
            finally {
                setProcessing(false);
            }
        });
    }
    if (promoCode) {
        return (React.createElement("div", { className: "store-promo-code" },
            React.createElement("div", { className: "store-promo-code__applied" },
                React.createElement("span", { className: "store-promo-code__tag" },
                    React.createElement(Icon, { value: "tag" }),
                    promoCode.code),
                promoCode.discountAmount > 0 && (React.createElement(Money, { className: "store-promo-code__amount", value: -promoCode.discountAmount })),
                React.createElement("button", { type: "button", className: "store-promo-code__remove", disabled: processing || disabled, onClick: handleRemove }, t('Remove'))),
            promoCode.status !== 'VALID' && (React.createElement("p", { className: "store-promo-code__warning", role: "alert" },
                React.createElement(Icon, { value: "circle-exclamation" }),
                promoCodeReasonMessage(promoCode.status))),
            error && (React.createElement("p", { className: "store-promo-code__error", role: "alert" }, error))));
    }
    if (!open) {
        return (React.createElement("div", { className: "store-promo-code" },
            React.createElement("button", { type: "button", className: "store-promo-code__toggle", disabled: disabled, onClick: () => setOpen(true) }, t('Have a promo code?'))));
    }
    return (React.createElement("div", { className: "store-promo-code" },
        React.createElement("div", { className: "store-promo-code__form" },
            React.createElement("input", { className: "store-promo-code__input", name: "promoCode", value: code, placeholder: t('Promo code'), "aria-label": t('Promo code'), autoComplete: "off", autoCapitalize: "characters", spellCheck: false, disabled: processing || disabled, onChange: (event) => {
                    setCode(event.target.value);
                    setError(null);
                }, onKeyDown: (event) => {
                    // The summary sits inside the order form: Enter must not submit it.
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        void handleApply();
                    }
                } }),
            React.createElement(Button, { style: "outline", size: "small", className: "store-promo-code__apply", label: t('Apply'), disabled: !canApply || disabled, processing: processing, onPress: handleApply })),
        !!code.trim() && (React.createElement(CaptchaWidget, { key: captchaKey, captcha: captcha, language: language, onVerify: setCaptchaToken })),
        error && (React.createElement("p", { className: "store-promo-code__error", role: "alert" }, error))));
}
